// ============================================================================
// Browser side of the server API.
//
// 1) `request()` — every call to the server: sends the CSRF header on writes,
//    turns error responses into Errors with a `code`, and announces a lost
//    session ("rundown:signed-out") so the app can show the sign-in screen.
//
// 2) `installCapabilities(session)` — provides window.claude.use("db" |
//    "assets" | "user" | "sample"), the interface the app was written
//    against, on top of this server. Beyond the original shape it adds:
//      - version tracking: a board/master/template/settings doc remembers the
//        version it was loaded at, and saves send it as If-Match, so a save
//        based on an out-of-date copy is refused (409) instead of silently
//        overwriting someone else's work. A conflict is announced as
//        "rundown:conflict" with the other person's current copy and a way
//        to overwrite anyway (the overwritten version stays in history).
//      - saves to the same document are queued, so quick successive autosaves
//        never race each other into a false conflict.
//      - live updates: on Cloudflare the server pushes "this changed" over
//        one WebSocket per tab (see hub/index.js), and only what changed is
//        fetched again; live show-control state arrives in the message
//        itself. Without the socket (other hosts, or while it reconnects)
//        it falls back to polling. Either way every fetch uses ETags, so
//        "nothing changed" costs a tiny 304, and pauses while the tab is
//        hidden.
//      - screens that watch the same document or query share one feed, so
//        two components watching "which day is live" cost one request.
// ============================================================================
const BASE = () => (typeof window !== "undefined" && window.__RUNDOWN_BASE__) || "";
const VERSIONED = new Set(["boards", "masters", "templates", "settings"]);

function emit(name, detail) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

async function request(path, { method = "GET", body, headers = {}, raw, quiet = false } = {}) {
  const h = { ...headers };
  if (method !== "GET") h["x-rundown-csrf"] = "1";
  let payload;
  if (raw !== undefined) payload = raw;
  else if (body !== undefined) { payload = JSON.stringify(body); h["content-type"] = "application/json"; }
  const res = await fetch(BASE() + path, { method, headers: h, body: payload, credentials: "same-origin" });
  if (res.status === 304) return { notModified: true, res };
  let json = null;
  const text = await res.text();
  try { json = text ? JSON.parse(text) : null; } catch (e) {}
  if (!res.ok) {
    const err = new Error((json && json.message) || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = (json && json.code) || (res.status >= 500 ? "unavailable" : "error");
    err.body = json;
    if (res.status === 401 && !path.startsWith("/api/auth/")) emit("rundown:signed-out", {});
    if (res.status === 403 && method !== "GET" && err.code === "forbidden" && !quiet) emit("rundown:forbidden", { message: err.message });
    throw err;
  }
  return { json, res };
}
const getJson = async (path) => (await request(path)).json;
const send = async (method, path, body) => (await request(path, { method, body })).json;

// ---- live updates: shared feeds, refreshed by push or by polling -------------
// Every onSnapshot() joins a feed keyed by what it watches. A feed fetches
// when it starts, when the server pushes a change to its collection, when the
// tab becomes visible again, and on a timer: fast while there's no push
// socket, only as a rare safety net while there is one.
const SAFETY_POLL_MS = { live: 30000, other: 300000 };
const feeds = new Map(); // key -> feed
const push = { ws: null, connected: false, stopped: true, failures: 0, opened: false, retryTimer: null, pingTimer: null };

function feedInterval(f) {
  if (push.connected) return f.col === "live" ? SAFETY_POLL_MS.live : SAFETY_POLL_MS.other;
  return f.fastMs;
}

function makeFeed(key, { col, docId, url, fastMs, toSnap, notFound }) {
  const f = { key, col, docId, url, fastMs, etag: null, version: -1, last: undefined, listeners: new Set(), timer: null, busy: false, again: false, pushedDuringFetch: false };
  const deliver = (value) => {
    f.last = value;
    for (const l of [...f.listeners]) { try { l.onNext(value); } catch (e) { console.error(e); } }
  };
  f.schedule = () => {
    clearTimeout(f.timer);
    if (f.listeners.size) f.timer = setTimeout(f.refresh, feedInterval(f));
  };
  f.refresh = async () => {
    if (!f.listeners.size) return;
    if (f.busy) { f.again = true; return; }
    clearTimeout(f.timer);
    if (document.hidden) return f.schedule();
    f.busy = true;
    try {
      const r = await request(url, { headers: f.etag ? { "if-none-match": f.etag } : {} });
      const v = docId !== null && r.json && typeof r.json.version === "number" ? r.json.version : null;
      // a newer version may have been pushed while this fetch was in flight
      const stale = f.pushedDuringFetch && v !== null && v < f.version;
      if (!r.notModified && !stale) {
        f.etag = r.res.headers.get("etag");
        if (v !== null) f.version = v;
        deliver(toSnap(r.json));
      }
    } catch (e) {
      if (e.status === 404 && notFound) { f.etag = null; f.version = 0; deliver(notFound()); }
      else for (const l of [...f.listeners]) if (l.onErr) l.onErr(e);
    } finally {
      f.busy = false;
      f.pushedDuringFetch = false;
    }
    if (f.again) { f.again = false; return f.refresh(); }
    f.schedule();
  };
  // a pushed document (live state) — no request needed. One older than what
  // this tab has is either out of order or the doc was deleted and recreated
  // while disconnected: let the server say which.
  f.apply = (msg) => {
    if (msg.v && msg.v < f.version) return f.refresh();
    if (f.busy) f.pushedDuringFetch = true;
    f.version = msg.v || 0;
    f.etag = msg.v ? `"v${msg.v}"` : null;
    deliver(snap(docId, msg.data === null ? undefined : msg.data));
    f.schedule();
  };
  return f;
}

function subscribe(key, opts, onNext, onErr) {
  let f = feeds.get(key);
  const isNew = !f;
  if (isNew) { f = makeFeed(key, opts); feeds.set(key, f); }
  const listener = { onNext, onErr };
  f.listeners.add(listener);
  if (isNew) f.refresh();
  else if (f.last !== undefined) Promise.resolve().then(() => { if (f.listeners.has(listener)) onNext(f.last); });
  return () => {
    f.listeners.delete(listener);
    if (!f.listeners.size) { clearTimeout(f.timer); feeds.delete(key); }
  };
}

const refreshAll = () => { for (const f of feeds.values()) f.refresh(); };

function onPushMessage(raw) {
  if (raw === "pong") return;
  let msg;
  try { msg = JSON.parse(raw); } catch (e) { return; }
  if (!msg || msg.t !== "change") return;
  for (const f of feeds.values()) {
    if (f.col !== msg.col) continue;
    if (f.docId === null) f.refresh();
    else if (f.docId === msg.id) { if ("data" in msg) f.apply(msg); else f.refresh(); }
  }
}

function connectPush() {
  if (push.stopped) return;
  const loc = window.location;
  let ws;
  try { ws = new WebSocket(`${loc.protocol === "https:" ? "wss:" : "ws:"}//${loc.host}${BASE()}/api/ws`); }
  catch (e) { return retryPush(); }
  push.ws = ws;
  ws.onopen = () => {
    if (push.ws !== ws) return;
    push.connected = true;
    push.opened = true;
    push.failures = 0;
    clearInterval(push.pingTimer);
    push.pingTimer = setInterval(() => { try { ws.send("ping"); } catch (e) {} }, 30000);
    refreshAll(); // catch up on anything missed while disconnected
  };
  ws.onmessage = (ev) => onPushMessage(ev.data);
  ws.onclose = () => {
    if (push.ws !== ws) return;
    const was = push.connected;
    push.ws = null;
    push.connected = false;
    clearInterval(push.pingTimer);
    if (was) refreshAll(); // back to polling at once, not after the long safety interval
    retryPush();
  };
}

function retryPush() {
  if (push.stopped) return;
  push.failures++;
  // a server that has never accepted the socket (no push on this host) is
  // retried rarely; a dropped one comes back quickly
  const cap = push.opened ? 30000 : 300000;
  const delay = Math.min(cap, 1000 * 2 ** Math.min(push.failures, 9)) * (0.5 + Math.random() / 2);
  clearTimeout(push.retryTimer);
  push.retryTimer = setTimeout(connectPush, delay);
}

function startPush() {
  if (!push.stopped || typeof WebSocket === "undefined") return;
  push.stopped = false;
  connectPush();
}

function stopPush() {
  push.stopped = true;
  clearTimeout(push.retryTimer);
  clearInterval(push.pingTimer);
  const ws = push.ws;
  push.ws = null;
  push.connected = false;
  if (ws) { try { ws.close(1000, "signed out"); } catch (e) {} }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (!document.hidden) refreshAll(); });
}

// ---- documents --------------------------------------------------------------------
const knownVersions = new Map(); // "col/id" -> version this tab last loaded/saved
const writeQueues = new Map();   // "col/id" -> promise tail

function queued(key, fn) {
  const prev = writeQueues.get(key) || Promise.resolve();
  const next = prev.then(fn, fn);
  const tail = next.catch(() => {});
  writeQueues.set(key, tail);
  return next;
}

const snap = (id, data) => ({ id, exists: data !== null && data !== undefined, data: () => data });
const docUrl = (col, id) => `/api/collections/${encodeURIComponent(col)}/${encodeURIComponent(id)}`;

function docRef(col, id) {
  const key = `${col}/${id}`;
  const url = docUrl(col, id);
  const versioned = VERSIONED.has(col);

  async function write(method, data, { force } = {}) {
    return queued(key, async () => {
      const headers = {};
      if (versioned && knownVersions.has(key)) headers["if-match"] = String(knownVersions.get(key));
      if (force !== undefined) headers["if-match"] = String(force);
      try {
        const { json } = await request(url, { method, body: data, headers });
        if (versioned) knownVersions.set(key, json.version);
        return json;
      } catch (e) {
        if (e.code === "conflict") {
          const current = e.body && e.body.current;
          e.conflict = { col, id, current };
          emit("rundown:conflict", {
            col, id, current, mine: data,
            // keep my copy: save it on top of the version that's there now (the
            // version I'm replacing is still kept in version history)
            overwrite: async () => {
              const v = e.body ? e.body.currentVersion : 0;
              await write("PUT", method === "PATCH" && current ? { ...current.data, ...data } : data, { force: v });
              emit("rundown:saved", { col, id });
            },
            // take theirs: forget my stale version so the editor can reload
            reload: () => { knownVersions.delete(key); emit("rundown:reload-doc", { col, id }); },
          });
        }
        throw e;
      }
    });
  }

  return {
    id,
    async get() {
      try {
        const { json } = await request(url);
        if (versioned) knownVersions.set(key, json.version);
        return snap(id, json.data);
      } catch (e) {
        if (e.status === 404) { if (versioned) knownVersions.set(key, 0); return snap(id, undefined); }
        throw e;
      }
    },
    async set(data) { await write("PUT", data); },
    async update(patch) { await write("PATCH", patch); },
    async delete() {
      await queued(key, async () => {
        const headers = versioned && knownVersions.has(key) ? { "if-match": String(knownVersions.get(key)) } : {};
        await request(url, { method: "DELETE", headers });
        knownVersions.delete(key);
      });
    },
    onSnapshot(onNext, onErr) {
      return subscribe(`doc ${url}`, {
        col, docId: id, url, fastMs: col === "live" ? 1000 : 3000,
        toSnap: (json) => snap(id, json.data),
        notFound: () => snap(id, undefined),
      }, onNext, onErr);
    },
  };
}

function queryRef(col, wheres, orderByField, orderByDir, fields) {
  const qs = () => {
    const p = new URLSearchParams();
    for (const w of wheres) p.append("where", w.join("|"));
    if (orderByField) { p.set("orderBy", orderByField); p.set("orderByDir", orderByDir || "asc"); }
    if (fields && fields.length) p.set("select", fields.join(","));
    const s = p.toString();
    return s ? `?${s}` : "";
  };
  const toSnap = (json) => {
    const docs = json.docs.map((d) => snap(d.id, d.data));
    return { docs, size: docs.length, empty: docs.length === 0 };
  };
  const self = {
    where(f, op, v) { return queryRef(col, [...wheres, [f, op, v]], orderByField, orderByDir, fields); },
    orderBy(f, dir) { return queryRef(col, wheres, f, dir || "asc", fields); },
    // only these top-level fields come back (server extension; the in-memory
    // test double doesn't have it, so callers check it exists)
    select(...names) { return queryRef(col, wheres, orderByField, orderByDir, names); },
    async get() { return toSnap(await getJson(`/api/collections/${encodeURIComponent(col)}${qs()}`)); },
    onSnapshot(onNext, onErr) {
      const url = `/api/collections/${encodeURIComponent(col)}${qs()}`;
      return subscribe(`query ${url}`, { col, docId: null, url, fastMs: col === "live" ? 2000 : 4000, toSnap }, onNext, onErr);
    },
  };
  return self;
}

function collectionRef(name) {
  const base = queryRef(name, [], null, "asc");
  return {
    doc: (id) => docRef(name, id),
    where: base.where, orderBy: base.orderBy, select: base.select, get: base.get, onSnapshot: base.onSnapshot,
    async add(data) {
      const json = await send("POST", `/api/collections/${encodeURIComponent(name)}`, data);
      if (VERSIONED.has(name)) knownVersions.set(`${name}/${json.id}`, json.version);
      return { id: json.id };
    },
  };
}

// ---- capabilities the app uses --------------------------------------------------------
function installCapabilities(session) {
  const dbCap = { collection: collectionRef };
  const assetsCap = {
    upload: async (blob, opts) => {
      const type = (opts && opts.type) || blob.type || "application/octet-stream";
      return (await request("/api/assets", { method: "POST", raw: blob, headers: { "content-type": type } })).json;
    },
    // best-effort cleanup when an image is replaced; an image someone else
    // uploaded may not be ours to delete, which is fine — no error toast
    delete: async (id) => { await request(`/api/assets/${encodeURIComponent(id)}`, { method: "DELETE", quiet: true }); },
  };
  const user = session.user;
  const userCap = {
    me: async () => ({ id: user.id, name: user.name, email: user.email }),
    isOwner: async () => user.role === "admin",
    can: async () => user.role !== "viewer",
    profiles: async (ids) => Object.fromEntries((ids || []).map((id) => [id, { name: id === user.id ? user.name : "" }])),
  };
  async function sampleJson(input, opts) {
    const form = new FormData();
    form.append("prompt", typeof input === "string" ? input : JSON.stringify(input));
    for (const f of (opts && opts.images) || []) form.append("images", f);
    return (await request("/api/sample/fight-card", { method: "POST", raw: form })).json;
  }
  // self-hosted-only extra: the server fetches a fight-card web page itself
  const sampleFromUrl = async (url) => send("POST", "/api/sample/fight-card-url", { url });
  const sampleCap = Object.assign(
    async (input, opts) => ({ text: JSON.stringify(await sampleJson(input, opts)), truncated: false }),
    { json: sampleJson, limits: () => getJson("/api/sample/limits"), fromUrl: sampleFromUrl }
  );
  window.claude = {
    async use(name) {
      if (name === "db") return dbCap;
      if (name === "assets") return assetsCap;
      if (name === "user") return userCap;
      if (name === "sample") return session.perms.ai ? sampleCap : null;
      return null;
    },
  };
  startPush();
}

// ---- auth / admin / history -----------------------------------------------------------
const Api = {
  config: () => getJson("/api/auth/config"),
  me: async () => {
    try { return await getJson("/api/auth/me"); } catch (e) { if (e.status === 401) return null; throw e; }
  },
  login: (email, password, remember) => send("POST", "/api/auth/login", { email, password, remember: !!remember }),
  signup: (fields) => send("POST", "/api/auth/signup", fields),
  setup: (fields) => send("POST", "/api/auth/setup", fields),
  logout: () => send("POST", "/api/auth/logout", {}),
  // Workers AI: match pasted notes to master cue rows (lib/aimatch.js)
  matchLane: (cues, text) => send("POST", "/api/ai/match-lane", { cues, text }),
  logoutEverywhere: () => send("POST", "/api/auth/logout-everywhere", {}),
  changePassword: (current, next) => send("POST", "/api/auth/password", { current, next }),
  googleStartUrl: () => `${BASE()}/api/auth/google/start`,
  listUsers: () => getJson("/api/admin/users"),
  listPeople: () => getJson("/api/people"),
  createUser: (u) => send("POST", "/api/admin/users", u),
  updateUser: (id, patch) => send("PATCH", `/api/admin/users/${encodeURIComponent(id)}`, patch),
  audit: (params) => getJson(`/api/admin/audit?${new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== ""))}`),
  history: (col, id) => getJson(`/api/history/${encodeURIComponent(col)}/${encodeURIComponent(id)}`),
  historyEntry: (col, id, v) => getJson(`/api/history/${encodeURIComponent(col)}/${encodeURIComponent(id)}/${v}`),
  restore: async (col, id, v) => {
    const r = await send("POST", `/api/history/${encodeURIComponent(col)}/${encodeURIComponent(id)}/${v}/restore`, {});
    knownVersions.set(`${col}/${id}`, r.version);
    return r;
  },
  getDoc: async (col, id) => {
    try { return await getJson(docUrl(col, id)); } catch (e) { if (e.status === 404) return null; throw e; }
  },
};

// after sign-out: forget everything this tab knew as the previous person
function resetClientState() {
  stopPush();
  for (const f of feeds.values()) clearTimeout(f.timer);
  feeds.clear();
  knownVersions.clear();
  writeQueues.clear();
  try { delete window.claude; } catch (e) { window.claude = undefined; }
}

export { request, installCapabilities, resetClientState, Api, knownVersions };
